import { useEffect, useState, useCallback } from "react";
import Header from "../components/Header";
import InteractiveMap from "../components/InteractiveMap";
import LotDetailDialog from "../components/LotDetailDialog";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MapPinned, Sparkles, CircleCheck } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1776971059473-9f7375f9c8cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxhZXJpYWwlMjByZXNpZGVudGlhbCUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc3NzE1MTAxOHww&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  const [lots, setLots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/lots");
      setLots(data);
    } catch (err) {
      console.error("Backend no disponible o error en la petición:", err);
      setLots([]); 
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onLotClick = (lot) => {
    setSelected(lot);
    setOpen(true);
  };

  const counts = lots.reduce(
    (acc, l) => {
      acc[l.status] = (acc[l.status] || 0) + 1;
      return acc;
    },
    { available: 0, reserved: 0, sold: 0 },
  );

  return (
    <div data-testid="home-page" className="min-h-screen flex flex-col">
      {/* Header completo para la vista normal */}
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Encuentra el lote              
              <br />
              <span className="text-slate-500">que el cliente desea.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-slate-600 leading-relaxed">
              Explora el mapa interactivo, consulta dimensiones y precios en
              tiempo real, y aparta su terreno en menos de un minuto.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#mapa"
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-5 py-3 rounded-md transition"
                data-testid="cta-explore"
              >
                Ver Mapa <ArrowRight size={16} />
              </a>
              <Link
                to="/status"
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-medium px-5 py-3 rounded-md transition"
                data-testid="cta-status"
              >
                Buscar Reservacion
              </Link>
            </div>
          </div>

          <div className="md:col-span-4 grid grid-cols-3 md:grid-cols-1 gap-3">
            <Stat label="Disponibles" value={counts.available} dot="bg-emerald-500" />
            <Stat label="Reservados" value={counts.reserved} dot="bg-amber-500" />
            <Stat label="Vendidos" value={counts.sold} dot="bg-red-500" />
          </div>
        </div>
      </section>

      {/* Map section */}
      <section id="mapa" className="max-w-7xl w-full mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
              <MapPinned size={12} /> Mapa interactivo
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              Toca un lote para apartarlo
            </h2>
          </div>
          <p className="text-sm text-slate-500 max-w-sm">
            Usa los controles para acercar / alejar. En móvil, pellizca para
            hacer zoom y arrastra para desplazarte.
          </p>
        </div>

        {loading ? (
          <div className="h-[60vh] min-h-[480px] border border-slate-200 rounded-lg bg-slate-50 grid place-items-center text-slate-400">
            Cargando mapa…
          </div>
        ) : (
          <InteractiveMap
            lots={lots}
            onLotClick={onLotClick} /* <-- Aquí está la conexión arreglada */
            selectedId={selected?.id}
          />
        )}
      </section>

      {/* Features */}
      <section className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-3 gap-8">
          <Feature
            icon={<CircleCheck size={20} />}
            title="Disponibilidad en vivo"
            text="El estado de cada lote se sincroniza automáticamente con nuestro sistema de ventas."
          />
          <Feature
            icon={<ShieldCheck size={20} />}
            title="Proceso transparente"
            text="Sube tu comprobante y da seguimiento desde la sección 'Mi reserva' con tu correo."
          />
          <Feature
            icon={<MapPinned size={20} />}
            title="Mapa preciso"
            text="Cada polígono representa exactamente tu lote, con superficie y precio listos para revisar."
          />
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Senderos de Providencia · Reservas inteligentes</span>
          <Link to="/admin/login" className="hover:text-slate-900 transition">
            Acceso administradores
          </Link>
        </div>
      </footer>

      <LotDetailDialog
        open={open}
        onOpenChange={setOpen}
        lot={selected}
        onReserved={() => load()}
      />
    </div>
  );
}

function Stat({ label, value, dot }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <span className={`inline-block h-2 w-2 rounded-full ${dot} mb-2`} />
      <p className="font-display text-3xl font-bold tracking-tight">{value}</p>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mt-1">
        {label}
      </p>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div>
      <div className="h-10 w-10 rounded-md bg-slate-900 text-white grid place-items-center mb-3">
        {icon}
      </div>
      <h3 className="font-display text-xl font-semibold tracking-tight mb-1">
        {title}
      </h3>
      <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}